import React, { useCallback, useState } from 'react';
import Alert from '@uber/base-icons/alert';
import { type Theme, styled, useStyletron } from 'baseui';
import { Avatar } from 'baseui/avatar';
import { Button, KIND as BTN_KIND, SHAPE, SIZE } from 'baseui/button';
import { ALIGN, HeaderNavigation, StyledNavigationItem, StyledNavigationList } from 'baseui/header-navigation';
import { Input } from 'baseui/input';
import { PLACEMENT, StatefulPopover } from 'baseui/popover';
import { LabelLarge, LabelXSmall, ParagraphSmall } from 'baseui/typography';
import { CollectCommentDialog } from '../../common/collect-comment-dialog';
import { useCollectCommentDialog } from '../../common/collect-comment-dialog/use-collect-comment-dialog';
import { SUBMIT_TASK_ACTION, TASK_TYPE } from '../consts/task';
import { type SUBMIT_TASK_ACTION_TYPE } from '../types/task';
const ClickableAvatar = styled('div', {
  cursor: 'pointer',
  ':hover': {
    opacity: 0.8
  }
});
const PopoverContent = styled('div', ({
  $theme
}: {
  $theme: Theme;
}) => ({
  padding: $theme.sizing.scale600,
  maxWidth: `${$theme.breakpoints.small}px`
}));
type CommonProps = {
  isTaskVisible?: boolean;
  userEmail?: string;
};
type WorkModeProps = {
  mode: typeof TASK_TYPE.WORK;
  onSubmit?: (action: SUBMIT_TASK_ACTION_TYPE) => void;
  onGetTask?: () => void;
  // Temporarily added to support the task id in work mode, will be replaced by TaskDetailsModal in future.
  workModeTaskId?: number;
  globalTaskId?: string;
  batchId?: string;
  isMetaReviewRejectEnabled?: boolean;
  onTaskReject: (rejectComment: string) => void;
  onCommentDialogClose: () => void;
  rejectComment?: string;
} & CommonProps;
type PreviewModeProps = {
  mode: typeof TASK_TYPE.PREVIEW;
  onPreviewTask?: (taskId: number) => void;
} & CommonProps;
type TaskHeaderProps = WorkModeProps | PreviewModeProps;
const TaskHeader = ({
  isTaskVisible,
  userEmail,
  ...props
}: TaskHeaderProps) => {
  const [_, theme] = useStyletron();
  const [taskId, setTaskId] = useState<string>('');
  const {
    showDialog,
    dialogProps
  } = useCollectCommentDialog();

  /**
   * Handle the task rejection submit. If the mode is work, calls the onTaskReject callback.
   * @param comment - The comment for the task rejection.
   * @returns void.
   */
  const handleTaskRejectSubmit = useCallback((comment: string) => {
    if (props.mode === TASK_TYPE.WORK) {
      props.onTaskReject(comment);
    }
  }, [props]);

  /**
   * Open the collect comment dialog to collect the comment for the task rejection.
   */
  const openCollectCommentDialog = useCallback(() => {
    showDialog({
      primaryButtonText: 'Reject Task',
      onSubmit: handleTaskRejectSubmit
    });
  }, [showDialog, handleTaskRejectSubmit]);
  return <HeaderNavigation data-testid="task-footer" overrides={{
    Root: {
      style: ({
        $theme
      }: {
        $theme: Theme;
      }) => ({
        padding: $theme.sizing.scale300
      })
    }
  }}>
      <StyledNavigationList $align={ALIGN.left}>
        <StyledNavigationItem>
          <LabelLarge>uLabel</LabelLarge>
        </StyledNavigationItem>
      </StyledNavigationList>
      <StyledNavigationList $align={ALIGN.center} />
      <StyledNavigationList $align={ALIGN.right}>
        {props.mode === TASK_TYPE.WORK && <>
            {!isTaskVisible ? <StyledNavigationItem>
                <ClickableAvatar data-testid="get-task">
                  <Avatar name={userEmail || '?'} size="scale900" overrides={{
              Root: {
                style: ({
                  $theme
                }) => ({
                  backgroundColor: $theme.colors.primary
                })
              }
            }} />
                </ClickableAvatar>
              </StyledNavigationItem> : <>
                {props.workModeTaskId && <StyledNavigationItem>
                    <LabelXSmall data-testid="task-id">Task ID: {props.workModeTaskId}</LabelXSmall>
                  </StyledNavigationItem>}
                {props.globalTaskId && <StyledNavigationItem>
                    <LabelXSmall data-testid="task-id">Global ID: {props.globalTaskId}</LabelXSmall>
                  </StyledNavigationItem>}
                {props.batchId && <StyledNavigationItem>
                    <LabelXSmall data-testid="task-id">Batch ID: {props.batchId}</LabelXSmall>
                  </StyledNavigationItem>}
                {props.rejectComment && <StyledNavigationItem>
                    <StatefulPopover content={<PopoverContent>
                          <ParagraphSmall>{props.rejectComment}</ParagraphSmall>
                        </PopoverContent>} placement={PLACEMENT.bottom} returnFocus autoFocus>
                      <Button data-testid="reject-task" size={SIZE.compact} shape={SHAPE.default} kind={BTN_KIND.secondary}>
                        <Alert size={16} />
                        <LabelXSmall style={{
                  marginLeft: theme.sizing.scale100
                }} color="inherit">
                          Review Comments
                        </LabelXSmall>
                      </Button>
                    </StatefulPopover>
                  </StyledNavigationItem>}
                <StyledNavigationItem>
                  <Button data-testid="pause-task" size={SIZE.compact} onClick={() => props.onSubmit?.(SUBMIT_TASK_ACTION.PAUSE)} shape={SHAPE.default} kind={BTN_KIND.secondary}>
                    Pause Task
                  </Button>
                </StyledNavigationItem>
                {props.isMetaReviewRejectEnabled && <>
                    <StyledNavigationItem>
                      <Button data-testid="reject-task" size={SIZE.compact} onClick={openCollectCommentDialog} shape={SHAPE.default} kind={BTN_KIND.secondary}>
                        Reject Task
                      </Button>
                    </StyledNavigationItem>
                    <CollectCommentDialog {...dialogProps} />
                  </>}
                <StyledNavigationItem>
                  <Button data-testid="submit-task" size={SIZE.compact} onClick={() => props.onSubmit?.(SUBMIT_TASK_ACTION.COMPLETE)} shape={SHAPE.default} kind={BTN_KIND.primary}>
                    Submit Task
                  </Button>
                </StyledNavigationItem>
              </>}
          </>}
        {props.mode === TASK_TYPE.PREVIEW && <>
            <StyledNavigationItem>
              <Input placeholder="Enter Task Id" onChange={element => {
            setTaskId(element.target.value);
          }} value={taskId} />
            </StyledNavigationItem>
            <StyledNavigationItem>
              <Button onClick={() => {
            if (!taskId) {
              return;
            }
            props.onPreviewTask?.(Number(taskId));
          }}>
                Get Task
              </Button>
            </StyledNavigationItem>
          </>}
      </StyledNavigationList>
    </HeaderNavigation>;
};
export default TaskHeader;